import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { StatusCertificadoEnum } from '@shared/enums/certificado/status-certificado.enum';
import { TipoAmbienteEnum } from '@shared/enums/dfe/tipo-ambiente.enum';
import { NotaFiscalSaidaErrorCodeEnum } from '@shared/enums/vendas/nota-fiscal-saida-error-code.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { CertificadoEmpresaA1Service } from '@shared/services/apis/dfe/certificado_empresa_a1.service';
import { ConfiguracaoDFeGeralService } from '@shared/services/apis/dfe/configuracao-dfe-geral.service';
import { NotaFiscalSaidaService } from '@shared/services/apis/orquestrador-vendas/nota-fiscal-saida.service';
import { DatetimeEmpresaPipe } from '@shared/pipes/datetime-empresa.pipe';

export interface NfeTransmitirDialogData {
  idNFeSaida: number;
  /** "NF-e 123 / 1 - Cliente": subtítulo do modal. */
  subtitulo: string;
  /** Ambiente do emissor da nota; nulo (listagem) usa o ambiente da configuração geral de notas fiscais. */
  isHomologacao: boolean | null;
  /** Aberto pelo documento: depois de enviar, o botão leva à listagem; pela linha, só fecha. */
  isDoDocumento: boolean;
}

export interface NfeTransmitirDialogResult {
  /** A nota foi para a fila (Em transmissão). */
  isEnviada: boolean;
  /** Documento: "Ir para a listagem". */
  isIrParaListagem: boolean;
  /** "Corrigir" depois da recusa: o motivo, para a tela levar à seção. Nulo quando não houve recusa. */
  errorCodeRecusa: NotaFiscalSaidaErrorCodeEnum | null;
}

/** Etapas do modal (estado local da tela). */
enum EtapaTransmitirNFe {
  Inicio = 1,
  Recusa = 2,
  Transmitindo = 3,
}

/**
 * Modal "Transmitir NF-e" (T13), aberto pelo documento e pela linha da listagem. Assíncrono: confirma, chama o
 * `transmitir` e, com a nota na fila, mostra "Transmitindo" e manda acompanhar o resultado pela listagem; não espera o
 * retorno da SEFAZ. Na recusa, lista as pendências devolvidas pelo servidor. Visual do `extracao-dados-modal`.
 */
@Component({
  selector: 'app-nfe-transmitir-dialog',
  standalone: true,
  imports: [DatetimeEmpresaPipe, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, AlertCardComponent, LoadingBoxComponent],
  templateUrl: './nfe-transmitir-dialog.component.html',
  styleUrls: ['./nfe-transmitir-dialog.component.css'],
})
export class NfeTransmitirDialogComponent implements OnInit {
  readonly etapaEnum = EtapaTransmitirNFe;
  readonly statusCertificadoEnum = StatusCertificadoEnum;

  etapa = EtapaTransmitirNFe.Inicio;
  isTransmitindo = false;
  isHomologacao: boolean | null = null;

  isCarregandoCertificado = true;
  statusCertificado: StatusCertificadoEnum | null = null;
  dataVencimentoCertificado: string | Date | null = null;

  listaPendencia: string[] = [];
  private errorCodeRecusa: NotaFiscalSaidaErrorCodeEnum | null = null;

  constructor(
    private dialogRef: MatDialogRef<NfeTransmitirDialogComponent, NfeTransmitirDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: NfeTransmitirDialogData,
    private notaFiscalSaidaService: NotaFiscalSaidaService,
    private certificadoEmpresaA1Service: CertificadoEmpresaA1Service,
    private configuracaoDFeGeralService: ConfiguracaoDFeGeralService,
    private alertService: AlertService,
  ) { }

  ngOnInit(): void {
    this.isHomologacao = this.data.isHomologacao;
    if (this.isHomologacao == null) this.carregarAmbiente();
    this.carregarCertificado();
  }

  /** Sem a nota do documento (listagem): o ambiente da configuração geral de notas fiscais. */
  private carregarAmbiente(): void {
    this.configuracaoDFeGeralService.get().subscribe({
      next: res => {
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.isHomologacao = res.configuracaoDFeGeral?.idTipoAmbiente === TipoAmbienteEnum.Homologacao;
      },
      error: () => {
        this.alertService.show('Aviso', 'Não foi possível consultar o ambiente de emissão agora.', AlertType.warning);
      },
    });
  }

  /** Situação do certificado A1 da empresa: só informa; quem recusa a transmissão é a SEFAZ. */
  private carregarCertificado(): void {
    this.certificadoEmpresaA1Service.getCertificadoAtual().subscribe({
      next: res => {
        this.isCarregandoCertificado = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.statusCertificado = res.statusCertificado;
        this.dataVencimentoCertificado = res.certificadoEmpresaA1?.dataVencimento ?? null;
      },
      error: () => {
        this.isCarregandoCertificado = false;
        this.alertService.show('Aviso', 'Não foi possível consultar o certificado digital agora.', AlertType.warning);
      },
    });
  }

  get isCertificadoComAviso(): boolean {
    return this.statusCertificado === StatusCertificadoEnum.ProximoDoVencimento
      || this.statusCertificado === StatusCertificadoEnum.VencimentoIminente;
  }

  get isCertificadoIndisponivel(): boolean {
    return this.statusCertificado === StatusCertificadoEnum.NaoConfigurado || this.statusCertificado === StatusCertificadoEnum.Vencido;
  }

  get rotuloBotaoFinal(): string {
    return this.data.isDoDocumento ? 'Ir para a listagem' : 'Fechar';
  }

  transmitir(): void {
    if (this.isTransmitindo) return;
    this.isTransmitindo = true;

    this.notaFiscalSaidaService.transmitir(this.data.idNFeSaida).subscribe({
      next: res => {
        this.isTransmitindo = false;
        if (res.isError) {
          // Recusa por etapa: listaPendencia traz as mensagens; sem a lista (falha de chamada), só o aviso e o modal segue no início.
          if (!res.listaPendencia?.length) {
            this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
            return;
          }
          this.listaPendencia = res.listaPendencia;
          this.errorCodeRecusa = res.errorCode;
          this.etapa = EtapaTransmitirNFe.Recusa;
          return;
        }
        this.etapa = EtapaTransmitirNFe.Transmitindo;
      },
      error: () => {
        this.isTransmitindo = false;
        this.alertService.show('Aviso', 'Não foi possível transmitir a nota agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  corrigir(): void {
    this.dialogRef.close({ isEnviada: false, isIrParaListagem: false, errorCodeRecusa: this.errorCodeRecusa });
  }

  concluir(): void {
    this.dialogRef.close({ isEnviada: true, isIrParaListagem: this.data.isDoDocumento, errorCodeRecusa: null });
  }

  /** O X: depois de enviada, a tela de origem ainda precisa saber que a nota foi para a fila. */
  fechar(): void {
    if (this.isTransmitindo) return;
    this.dialogRef.close({ isEnviada: this.etapa === EtapaTransmitirNFe.Transmitindo, isIrParaListagem: false, errorCodeRecusa: null });
  }
}
